/**
 * Project favorites are a per-user, per-organization preference.
 * A favorite asks CrewPilot to keep that project's lightweight offline
 * package current. The package extends the field snapshot and the existing
 * picture sync database. It does not download full-resolution originals.
 */

import { loadProjectTasks } from "./tasks/projectTasks";
import { supabase } from "../supabase";
import { canAttemptSync } from "./connectivity";
import { PROJECT_FAVORITES_KEY } from "./offlineCacheScope";
import {
  readOrgScopedJson,
  resolveCacheOrganizationId,
  writeOrgScopedJson,
} from "./orgCacheStorage";
import { resolveOrganizationIdForQueries, applyOrganizationScope } from "./orgQueryScope";
import { getProjectsRelationName } from "./operationalProjections";
import { assertCan, CAPABILITY } from "./permissions";
import { loadPictureAuthorNames, PROJECT_PICTURES_BUCKET } from "./projectPictures";
import { rememberFieldProject } from "./projectFieldSnapshot";
import { loadProjectStructure } from "./projectStructure";
import {
  listCachedProjectPictures,
  listLocalPictures,
  replaceCachedProjectComments,
  replaceCachedProjectPictures,
  readCachedPictureThumb,
} from "./pictureSyncStore";

export { PROJECT_FAVORITES_KEY };

export const FAVORITE_PACKAGE = Object.freeze({
  PREPARING: "preparing",
  READY: "ready",
  ATTENTION: "attention",
});

const FAVORITE_EVENT = "crewpilot-project-favorites";

let refreshChain = Promise.resolve();

function actorUserId(actor) {
  return actor?.supabase_user_id || "";
}

function actorOrgId(actor) {
  return resolveCacheOrganizationId(actor) || "";
}

function emptyRecord(actor) {
  return {
    userId: actorUserId(actor),
    organizationId: actorOrgId(actor),
    projectIds: [],
    pendingAdds: [],
    pendingRemoves: [],
    ready: {},
    savedAt: "",
  };
}

function readRecord(actor) {
  const organizationId = actorOrgId(actor);
  const userId = actorUserId(actor);
  if (!organizationId || !userId) return null;
  const raw = readOrgScopedJson(PROJECT_FAVORITES_KEY, organizationId);
  if (!raw || raw.userId !== userId || raw.organizationId !== organizationId) return null;
  return {
    ...emptyRecord(actor),
    ...raw,
    projectIds: Array.isArray(raw.projectIds) ? raw.projectIds : [],
    pendingAdds: Array.isArray(raw.pendingAdds) ? raw.pendingAdds : [],
    pendingRemoves: Array.isArray(raw.pendingRemoves) ? raw.pendingRemoves : [],
    ready: raw.ready && typeof raw.ready === "object" ? raw.ready : {},
  };
}

function writeRecord(actor, record) {
  const organizationId = actorOrgId(actor);
  const userId = actorUserId(actor);
  if (!organizationId || !userId) return;
  writeOrgScopedJson(PROJECT_FAVORITES_KEY, organizationId, {
    userId,
    organizationId,
    projectIds: record.projectIds || [],
    pendingAdds: record.pendingAdds || [],
    pendingRemoves: record.pendingRemoves || [],
    ready: record.ready || {},
    savedAt: new Date().toISOString(),
  });
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(FAVORITE_EVENT));
  }
}

function uniqueIds(ids) {
  return [...new Set((ids || []).filter(Boolean))];
}

export function readFavoriteProjectIds(actor) {
  return readRecord(actor)?.projectIds || [];
}

export function readFavoriteReadyMap(actor) {
  return { ...(readRecord(actor)?.ready || {}) };
}

export function favoriteStatusLabel(state) {
  if (state === FAVORITE_PACKAGE.PREPARING) return "Preparing offline…";
  if (state === FAVORITE_PACKAGE.READY) return "Ready offline";
  if (state === FAVORITE_PACKAGE.ATTENTION) return "Offline copy needs a refresh";
  return "";
}

export function isProjectFavorite(actor, projectId) {
  return readFavoriteProjectIds(actor).includes(projectId);
}

export function partitionFavoriteHubProjects(projects, favoriteIds) {
  const ids = new Set(favoriteIds || []);
  const favorites = [];
  const rest = [];
  (projects || []).forEach((project) => {
    if (ids.has(project?.project_id)) favorites.push(project);
    else rest.push(project);
  });
  return { favorites, rest };
}

export function mergePictureComments(cached, pending) {
  const byId = new Map();
  (cached || []).forEach((row) => {
    if (!row?.comment_id || row.is_active === false) return;
    byId.set(row.comment_id, { ...row, pending_sync: false });
  });
  (pending || []).forEach((row) => {
    if (!row?.comment_id) return;
    byId.set(row.comment_id, { ...row, pending_sync: true, is_active: row.is_active !== false });
  });
  return [...byId.values()].sort((a, b) => String(a.created_at || "").localeCompare(String(b.created_at || "")));
}

export async function countOfflineProjectPictures(actor, projectId) {
  const userId = actorUserId(actor);
  const organizationId = actorOrgId(actor);
  const cached = await listCachedProjectPictures({ userId, organizationId, projectId });
  const pending = await listLocalPictures({ organizationId, createdBy: userId, projectId });
  const cachedIds = new Set(cached.map((row) => row.picture_id));
  const extra = pending.filter((row) => row.sync_state !== "synced" && !cachedIds.has(row.picture_id));
  return cached.length + extra.length;
}

function setReady(actor, projectId, state) {
  const current = readRecord(actor) || emptyRecord(actor);
  writeRecord(actor, {
    ...current,
    ready: { ...current.ready, [projectId]: state },
  });
}

function applyLocalToggle(actor, projectId, favorite) {
  const current = readRecord(actor) || emptyRecord(actor);
  const pendingAdds = new Set(current.pendingAdds);
  const pendingRemoves = new Set(current.pendingRemoves);
  const projectIds = new Set(current.projectIds);
  if (favorite) {
    projectIds.add(projectId);
    pendingAdds.add(projectId);
    pendingRemoves.delete(projectId);
  } else {
    projectIds.delete(projectId);
    pendingRemoves.add(projectId);
    pendingAdds.delete(projectId);
    const ready = { ...current.ready };
    delete ready[projectId];
    writeRecord(actor, {
      ...current,
      projectIds: [...projectIds],
      pendingAdds: [...pendingAdds],
      pendingRemoves: [...pendingRemoves],
      ready,
    });
    return;
  }
  writeRecord(actor, {
    ...current,
    projectIds: [...projectIds],
    pendingAdds: [...pendingAdds],
    pendingRemoves: [...pendingRemoves],
  });
}

async function readServerFavoriteIds(actor, loadPreferences) {
  const loaded = await loadPreferences(actor);
  const map = loaded?.preferences?.favoriteProjects;
  const ids = map?.[actorOrgId(actor)];
  return Array.isArray(ids) ? uniqueIds(ids) : [];
}

async function defaultLoadPreferences(actor) {
  const userId = actorUserId(actor);
  if (!userId) return null;
  const { data, error } = await supabase
    .from("profiles")
    .select("id, preferences")
    .eq("supabase_user_id", userId)
    .maybeSingle();
  if (error) throw error;
  return data ? { profileId: data.id, preferences: data.preferences || {} } : null;
}

async function defaultSavePreferences(actor, projectIds, loaded) {
  if (!loaded?.profileId) throw new Error("Profile preferences are unavailable.");
  const preferences = { ...(loaded.preferences || {}) };
  const favoriteProjects = { ...(preferences.favoriteProjects || {}) };
  favoriteProjects[actorOrgId(actor)] = uniqueIds(projectIds);
  preferences.favoriteProjects = favoriteProjects;
  const { error } = await supabase
    .from("profiles")
    .update({ preferences })
    .eq("id", loaded.profileId);
  if (error) throw error;
}

export async function syncFavoritePreferences(actor, deps = {}) {
  const loadPreferences = deps.loadPreferences || defaultLoadPreferences;
  const savePreferences = deps.savePreferences || defaultSavePreferences;
  const local = readRecord(actor) || emptyRecord(actor);
  if (!canAttemptSync()) return local.projectIds;
  let loaded;
  let serverIds;
  try {
    loaded = await loadPreferences(actor);
    serverIds = await readServerFavoriteIds(actor, async () => loaded);
  } catch {
    return local.projectIds;
  }
  const pendingAdds = new Set(local.pendingAdds);
  const pendingRemoves = new Set(local.pendingRemoves);
  let next = serverIds.filter((id) => !pendingRemoves.has(id));
  pendingAdds.forEach((id) => {
    if (!next.includes(id)) next.push(id);
  });
  next = uniqueIds(next);
  const changed = pendingAdds.size || pendingRemoves.size
    || next.length !== serverIds.length
    || next.some((id, index) => id !== serverIds[index]);
  if (changed) {
    try {
      await savePreferences(actor, next, loaded);
    } catch {
      return local.projectIds;
    }
  }
  writeRecord(actor, {
    ...local,
    projectIds: next,
    pendingAdds: [],
    pendingRemoves: [],
  });
  return next;
}

async function defaultLoadProject(actor, projectId) {
  const organizationId = await resolveOrganizationIdForQueries(actor, supabase);
  const relation = getProjectsRelationName(actor);
  const projectSelect = relation === "projects_operational"
    ? "project_id, project_name, client_id, created_by, notes, organization_id, is_active, status, project_number, site_address, client_name"
    : "project_id, project_name, client_id, created_by, notes, organization_id, is_active, status, project_number, site_address, clients(client_name)";
  let query = supabase.from(relation).select(projectSelect).eq("project_id", projectId);
  query = applyOrganizationScope(query, organizationId, { tableName: "projects" });
  const { data, error } = await query.maybeSingle();
  if (error) throw error;
  return data;
}

async function defaultLoadPictures(actor, projectId) {
  assertCan(actor, CAPABILITY.PROJECT_VIEW);
  const organizationId = await resolveOrganizationIdForQueries(actor, supabase);
  const { data, error } = await supabase
    .from("project_pictures")
    .select("picture_id, project_id, location_id, organization_id, created_by, captured_at, created_at, is_active, thumbnail_path, content_type, file_name")
    .eq("organization_id", organizationId)
    .eq("project_id", projectId)
    .eq("is_active", true)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return data || [];
}

async function defaultLoadComments(actor, projectId) {
  assertCan(actor, CAPABILITY.PROJECT_VIEW);
  const organizationId = await resolveOrganizationIdForQueries(actor, supabase);
  const { data, error } = await supabase
    .from("project_picture_comments")
    .select("comment_id, picture_id, project_id, organization_id, body, source, created_by, created_at, is_active")
    .eq("organization_id", organizationId)
    .eq("project_id", projectId)
    .eq("is_active", true)
    .order("created_at", { ascending: true });
  if (error) throw error;
  return data || [];
}

async function defaultDownloadThumb(path) {
  if (!path) return null;
  const { data, error } = await supabase.storage.from(PROJECT_PICTURES_BUCKET).download(path);
  if (error) return null;
  return data || null;
}

async function namesFor(actor, ids, loadNames) {
  try {
    return await loadNames(actor, ids);
  } catch {
    return {};
  }
}

export async function prepareFavoriteProject(actor, projectId, deps = {}) {
  if (!projectId || !actorUserId(actor) || !actorOrgId(actor)) {
    return { ok: false };
  }
  const announce = deps.announce === true;
  if (announce) setReady(actor, projectId, FAVORITE_PACKAGE.PREPARING);
  const loadProject = deps.loadProject || defaultLoadProject;
  const loadPictures = deps.loadPictures || defaultLoadPictures;
  const loadComments = deps.loadComments || defaultLoadComments;
  const downloadThumb = deps.downloadThumb || defaultDownloadThumb;
  const loadNames = deps.loadNames || loadPictureAuthorNames;
  const loadLocations = deps.loadLocations || loadProjectStructure;
  const loadTasks = deps.loadTasks || ((a, id) => loadProjectTasks(a, id));

  try {
    const project = await loadProject(actor, projectId);
    if (!project) {
      setReady(actor, projectId, FAVORITE_PACKAGE.ATTENTION);
      return { ok: false };
    }
    rememberFieldProject(actor, project);
    await loadLocations(actor, projectId);
    // Tasks are part of the offline copy (read-only). Best effort: an
    // environment without the Tasks tables must never turn a Ready package
    // into "needs a refresh".
    let tasksTimer;
    try {
      await Promise.race([
        loadTasks(actor, projectId),
        new Promise((_, reject) => {
          tasksTimer = setTimeout(() => reject(new Error("tasks timeout")), 10000);
        }),
      ]);
    } catch (taskError) {
      console.warn("[favorites] tasks not cached", { code: taskError?.code || null });
    } finally {
      clearTimeout(tasksTimer);
    }
    const pictures = await loadPictures(actor, projectId);
    const comments = await loadComments(actor, projectId);
    const nameIds = [
      ...pictures.map((row) => row.created_by),
      ...comments.map((row) => row.created_by),
    ];
    const names = await namesFor(actor, nameIds, loadNames);
    const withNames = pictures.map((row) => ({
      ...row,
      author_name: names[row.created_by] || row.author_name || "",
    }));
    const thumbsById = {};
    for (const row of withNames) {
      if (!row.thumbnail_path) continue;
      const already = await readCachedPictureThumb(actorUserId(actor), row.picture_id);
      if (already && deps.missingThumbsOnly !== false) {
        continue;
      }
      const blob = await downloadThumb(row.thumbnail_path);
      if (blob) thumbsById[row.picture_id] = blob;
    }
    await replaceCachedProjectPictures({
      userId: actorUserId(actor),
      organizationId: actorOrgId(actor),
      projectId,
      pictures: withNames,
      thumbsById,
    });
    await replaceCachedProjectComments({
      userId: actorUserId(actor),
      organizationId: actorOrgId(actor),
      projectId,
      comments: comments.map((row) => ({
        ...row,
        author_name: names[row.created_by] || "",
      })),
    });
    setReady(actor, projectId, FAVORITE_PACKAGE.READY);
    return { ok: true, pictures: withNames.length };
  } catch (error) {
    // Never log project/financial data: only the failing code and message.
    console.warn("[favorites] offline package refresh failed", {
      code: error?.code || null,
      message: String(error?.message || error).slice(0, 160),
    });
    setReady(actor, projectId, FAVORITE_PACKAGE.ATTENTION);
    return { ok: false };
  }
}

export async function refreshFavoriteProjects(actor, deps = {}) {
  if (!actor || actor.isGuest || !actorUserId(actor) || !actorOrgId(actor)) return [];
  if (!canAttemptSync()) return readFavoriteProjectIds(actor);
  const run = refreshChain.then(async () => {
    const ids = await syncFavoritePreferences(actor, deps);
    for (const projectId of ids) {
      const known = readFavoriteReadyMap(actor)[projectId];
      await prepareFavoriteProject(actor, projectId, {
        ...deps,
        announce: known !== FAVORITE_PACKAGE.READY,
      });
    }
    return ids;
  });
  refreshChain = run.then(() => undefined, () => undefined);
  return run;
}

export async function toggleProjectFavorite(actor, projectId, deps = {}) {
  if (!projectId || !actorUserId(actor) || actor?.isGuest) return null;
  const favorite = !isProjectFavorite(actor, projectId);
  applyLocalToggle(actor, projectId, favorite);
  if (!canAttemptSync()) return { favorite, ready: readFavoriteReadyMap(actor)[projectId] || "" };
  await syncFavoritePreferences(actor, deps);
  if (favorite) {
    await prepareFavoriteProject(actor, projectId, { ...deps, announce: true });
  }
  return { favorite, ready: readFavoriteReadyMap(actor)[projectId] || "" };
}

export async function retryFavoriteProject(actor, projectId, deps = {}) {
  if (!isProjectFavorite(actor, projectId) || !canAttemptSync()) return { ok: false };
  return prepareFavoriteProject(actor, projectId, { ...deps, announce: true });
}

export function __resetFavoriteRefreshForTests() {
  refreshChain = Promise.resolve();
}
